import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Globe } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { NAV_LINKS as LINKS } from "@/data/site";
import { useT } from "@/i18n/LanguageProvider";

const linkTestId = {
  home: NAV.linkHome,
  about: NAV.linkAbout,
  services: NAV.linkServices,
  work: NAV.linkWork,
  process: NAV.linkProcess,
  contact: NAV.linkContact,
};

function LangSwitch({ variant = "desktop" }) {
  const { lang, setLang } = useT();
  const next = lang === "en" ? "zh" : "en";
  const label = lang === "en" ? "EN" : "中";
  const nextLabel = lang === "en" ? "中" : "EN";

  return (
    <button
      data-testid="nav-lang-switch"
      onClick={() => setLang(next)}
      aria-label={`Switch language to ${nextLabel}`}
      className={
        variant === "desktop"
          ? "group hidden md:inline-flex items-center gap-2 rounded-full border border-white/10 px-3.5 py-2 text-xs text-white/70 hover:text-white hover:border-white/25 transition-colors duration-300 font-body tracking-wider"
          : "inline-flex items-center gap-2 rounded-full border border-white/10 px-3.5 py-2 text-xs text-white/80 font-body tracking-wider"
      }
    >
      <Globe className="w-3.5 h-3.5 opacity-70 group-hover:opacity-100" />
      <span className="tabular-nums">{label}</span>
      <span className="text-white/30">/</span>
      <span className="text-white/40 group-hover:text-white/70 transition-colors">{nextLabel}</span>
    </button>
  );
}

export default function Nav() {
  const { t } = useT();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToContact = () => {
    setOpen(false);
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid={NAV.root}
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
        scrolled
          ? "bg-black/60 backdrop-blur-xl border-b border-white/[0.06]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-24 md:h-28 flex items-center justify-between">
        <a
          href="#home"
          data-testid={NAV.logo}
          aria-label="Wen Jie Studio"
          className="flex items-center gap-4 md:gap-5 group"
        >
          <span
            className="logo-shine shrink-0"
            style={{ "--logo-src": "url(/logo-wj.webp)" }}
          >
            <img
              src="/logo-wj.webp"
              alt="Wen Jie Studio"
              width="70"
              height="80"
              className="h-[56px] md:h-[66px] w-auto select-none"
              draggable="false"
              decoding="async"
              fetchPriority="high"
            />
            <img
              src="/logo-wj-accent.webp"
              alt=""
              aria-hidden="true"
              className="logo-accent"
              draggable="false"
              decoding="async"
            />
          </span>
          <span
            aria-hidden="true"
            className="hidden sm:block h-10 md:h-12 w-px bg-white/15 shrink-0"
          />
          <span className="flex flex-col leading-none font-display text-white select-none">
            <span className="text-[15px] md:text-[19px] tracking-[0.22em] font-light">
              WEN JIE
            </span>
            <span className="mt-1 text-[15px] md:text-[19px] tracking-[0.22em] font-light">
              STUDIO
            </span>
            <span
              aria-hidden="true"
              className="mt-2 h-px w-[168px] md:w-[210px] bg-white/70"
            />
            <span className="mt-2 text-[9px] md:text-[10.5px] tracking-[0.32em] text-white/80 font-body">
              <span className="tagline-word tagline-word--design">DESIGN</span>
              <span> </span>
              <span className="tagline-word tagline-word--build">BUILD</span>
              <span> </span>
              <span className="tagline-word tagline-word--launch">LAUNCH</span>
            </span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-1">
          {LINKS.map((link) => (
            <a
              key={link.key}
              href={link.href}
              data-testid={linkTestId[link.key]}
              className="relative px-4 py-2 text-sm text-white/60 hover:text-white transition-colors duration-300 font-body group"
            >
              <span>{t(`nav.${link.key}`)}</span>
              <span className="absolute left-4 right-4 -bottom-0.5 h-px bg-white/40 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <LangSwitch />
          <button
            data-testid={NAV.cta}
            onClick={goToContact}
            className="group hidden md:inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium text-black bg-white hover:bg-white/90 transition-colors duration-300"
          >
            {t("nav.cta")}
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>

          <button
            data-testid={NAV.mobileToggle}
            aria-label="Open menu"
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-white/10 text-white"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={NAV.mobileMenu}
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden border-t border-white/[0.06] bg-black/85 backdrop-blur-xl"
          >
            <nav className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-1">
              {LINKS.map((link) => (
                <a
                  key={link.key}
                  href={link.href}
                  data-testid={linkTestId[link.key]}
                  onClick={() => setOpen(false)}
                  className="px-3 py-3 text-base text-white/80 hover:text-white transition-colors font-body"
                >
                  {t(`nav.${link.key}`)}
                </a>
              ))}
              <div className="mt-3 flex items-center gap-3">
                <LangSwitch variant="mobile" />
                <button
                  onClick={goToContact}
                  data-testid={`${NAV.cta}-mobile`}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium text-black bg-white"
                >
                  {t("nav.cta")} <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
